import React, { Context, createContext, ReactElement } from 'react'
import { UserInfo, useUserInfo } from '../../../ReactRouting/Hooks/useUserInfo'
import { AdminUserResponse } from '../../../NonReact/common/Types/Types'

/**
 * User permission names which will be sent in current user response.
 */
export enum Permissions {
    DEVICE_CREATE = 'Device|Create',
    DEVICE_READ = 'Device|Read',
    DEVICE_UPDATE = 'Device|Update',
    DEVICE_DELETE = 'Device|Delete',
    ACDAGENT_CREATE = 'ACDAgent|Create',
    ACDAGENT_READ = 'ACDAgent|Read',
    ACDAGENT_UPDATE = 'ACDAgent|Update',
    ACDAGENT_DELETE = 'ACDAgent|Delete',
    CODE_CREATE = 'Code|Create',
    CODE_READ = 'Code|Read',
    CODE_UPDATE = 'Code|Update',
    CODE_DELETE = 'Code|Delete',
    DIRECTORY_ENTRY_CREATE = 'DirectoryEntry|Create',
    DIRECTORY_ENTRY_READ = 'DirectoryEntry|Read',
    DIRECTORY_ENTRY_UPDATE = 'DirectoryEntry|Update',
    DIRECTORY_ENTRY_DELETE = 'DirectoryEntry|Delete',
    PARTITION_CREATE = 'Partition|Create',
    PARTITION_READ = 'Partition|Read',
    PARTITION_UPDATE = 'Partition|Update',
    PARTITION_DELETE = 'Partition|Delete',
    TEL_SYS_CREATE = 'TelSys|Create',
    TEL_SYS_READ = 'TelSys|Read',
    TEL_SYS_UPDATE = 'TelSys|Update',
    TEL_SYS_DELETE = 'TelSys|Delete',
    USER_CREATE = 'User|Create',
    USER_READ = 'User|Read',
    USER_UPDATE = 'User|Update',
    USER_DELETE = 'User|Delete',
    RECORDING_CREATE = 'Recording|Create',
    RECORDING_READ = 'Recording|Read',
    RECORDING_UPDATE = 'Recording|Update',
    RECORDING_DELETE = 'Recording|Delete',
    CME_CREATE = 'CME|Create',
    CME_READ = 'CME|Read',
    CME_UPDATE = 'CME|Update',
    CME_DELETE = 'CME|Delete',
    MESSAGE_CREATE = 'Message|Create',
    MESSAGE_READ = 'Message|Read',
    MESSAGE_UPDATE = 'Message|Update',
    MESSAGE_DELETE = 'Message|Delete',
    DATABASE_CREATE = 'Database|Create',
    DATABASE_READ = 'Database|Read',
    DATABASE_UPDATE = 'Database|Update',
    DATABASE_DELETE = 'Database|Delete',
    LOG_MANAGEMENT_CREATE = 'LogMngmt|Create',
    LOG_MANAGEMENT_READ = 'LogMngmt|Read',
    LOG_MANAGEMENT_UPDATE = 'LogMngmt|Update',
    LOG_MANAGEMENT_DELETE = 'LogMngmt|Delete',
    CALL_CONTROL_CREATE = 'CallControl|Create',
    CALL_CONTROL_READ = 'CallControl|Read',
    CALL_CONTROL_UPDATE = 'CallControl|Update',
    CALL_CONTROL_DELETE = 'CallControl|Delete',
    REPORT_CREATE = 'Report|Create',
    REPORT_READ = 'Report|Read',
    REPORT_UPDATE = 'Report|Update',
    REPORT_DELETE = 'Report|Delete',
    LICENSE_CREATE = 'License|Create',
    LICENSE_READ = 'License|Read',
    LICENSE_UPDATE = 'License|Update',
    LICENSE_DELETE = 'License|Delete',
    LOGGING_CREATE = 'Logging|Create',
    LOGGING_READ = 'Logging|Read',
    LOGGING_UPDATE = 'Logging|Update',
    LOGGING_DELETE = 'Logging|Delete',
    SIGN_IN_AS = 'SignInAs',
    REPORT_COPY = 'ReportCopy',
    AUDIT_TRAIL = 'AuditTrail|Read',
    TELSYS_STATUS = 'TelSysStatus|Read',
    OMNICHANNEL_STATUS = 'OmnichannelStatus|Read',
    SERVER_STATUS = 'ServerStatus|Read',
    TELSYS_PARTITION_LICENSE_LEVEL = 'ChangeTelSysPartitionLicenseLevel',
    BILLING_SUMMARY = 'BillingSummary',
    BILLING_CDRs = 'BillingCDRs',
    PARTITION_SYNC = 'SyncPartition',
    REPORT_STD = 'ReportSTD',
    REPORT_ACD = 'ReportACD',
    REPORT_HISTORIC = 'ReportHistoric',
    REPORT_BLF = 'ReportBLF',
    REPORT_OMNICHANNEL = 'ReportOmni',
    REPORT_DATA_API = 'ReportDataAPI',
    CONCURRENT_SIGN_IN = 'ConcurrentSignIn',
    SERVICE_NOTIFICATION = 'ServiceNotification',
    MOBILE_APP_FEATURES = 'MobileAppFeatures',
    OMNICHANNEL_ACD_AGENT = 'OmnichannelACDAgent',
    EPT_ACCESS = 'EPTAccess',
    LOG_FILES_DOWNLOAD = 'LogFilesDownload'
}

/**
 * User info context type.
 *
 * @deprecated - use `const [userInfo]: UserInfo = useUserInfo()` instead.
 */
export type UserInfoContextType = Definable<AdminUserResponse>

/**
 * Component properties type.
 */
type Props = {
    disableError?: boolean
    children: React.ReactNode
}

/**
 * User info provider.
 *
 * @param disableError - disable error.
 * @param children - children.
 * @deprecated - use `const [userInfo]: UserInfo = useUserInfo()` instead.
 */
export const UserInfoContextProvider = ({ disableError, children }: Props): ReactElement => {
    const [userInfo]: UserInfo = useUserInfo(disableError)

    return <UserInfoContext.Provider value={userInfo}>{children}</UserInfoContext.Provider>
}

/**
 * Context for current user info.
 *
 * @deprecated - use `const [userInfo]: UserInfo = useUserInfo()` instead.
 */
const UserInfoContext: Context<UserInfoContextType> = createContext<UserInfoContextType>(undefined)
export default UserInfoContext
